import { useState } from "react";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import useModal from "../../hooks/useModal";

// Modal body for the employee's own grievance actions (Withdraw, Reopen):
// a prompt, an optional reason, and Cancel / confirm. The reason is written
// to the activity log by the backend. Shown from both the grievance list and
// the details page.
//   submit(reason)  -> the API call, e.g. withdrawGrievance(id, reason)
//   onDone()        -> refresh callback after a successful submit
const EmployeeActionForm = ({
  prompt,
  confirmText,
  confirmVariant = "danger",
  busyText,
  successMessage,
  errorMessage,
  submit,
  onDone,
  reasonLabel = "Reason (optional)",
}) => {
  const { hideModal } = useModal();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await submit(reason);
      toast.success(successMessage);
      hideModal();
      await onDone?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-2">
      <p className="mb-3">{prompt}</p>
      <TextField
        fullWidth
        multiline
        minRows={3}
        variant="standard"
        label={reasonLabel}
        inputProps={{ maxLength: 1000 }}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="d-flex justify-content-end mt-3 gap-2">
        <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
          Cancel
        </Button>
        <Button variant={confirmVariant} onClick={handleSubmit} disabled={loading}>
          {loading ? busyText : confirmText}
        </Button>
      </div>
    </div>
  );
};

// Preset props for each action, so both screens open identical modals.
export const withdrawFormProps = (submit) => ({
  prompt:
    "Are you sure you want to withdraw this grievance? This cannot be undone.",
  confirmText: "Yes, Withdraw",
  confirmVariant: "danger",
  busyText: "Withdrawing...",
  successMessage: "Grievance withdrawn successfully",
  errorMessage: "Failed to withdraw grievance",
  submit,
});

export const reopenFormProps = (submit) => ({
  prompt:
    "Reopen this grievance? HR will be notified and will review it again.",
  confirmText: "Yes, Reopen",
  confirmVariant: "success",
  busyText: "Reopening...",
  successMessage: "Grievance reopened successfully",
  errorMessage: "Failed to reopen grievance",
  submit,
});

export const acceptFormProps = (submit) => ({
  prompt:
    "Accept HR's resolution? This closes the grievance. You can still reopen it afterwards if the problem comes back.",
  confirmText: "Yes, Accept",
  confirmVariant: "success",
  busyText: "Accepting...",
  successMessage: "Resolution accepted — grievance closed",
  errorMessage: "Failed to accept resolution",
  submit,
  reasonLabel: "Comment (optional)",
});

export default EmployeeActionForm;
